import React from 'react';
import { IconType } from 'react-icons';

import {
  FaUtensils,
  FaEdit,
  FaCalendar,
  FaWeight,
  FaHeartbeat,
  FaTrash,
  FaPlus,
  FaSmile,
  FaHeadset,
  FaCheck,
} from 'react-icons/fa';
import { FunctionComponent } from 'react';

interface Props {
  isLoggedIn?: boolean;
}

interface Feature {
  Icon: FunctionComponent<{ className?: string }>;
  title: string;
  description: string;
  benefits: string[];
  screenshot?: string;
  screenshotAlt?: string;
}

interface FeatureCategory {
  category: string;
  features: Feature[];
}

const Features: React.FC<Props> = React.memo(({ isLoggedIn = false }) => {
  const FaCheckIcon = FaCheck as FunctionComponent<{ className?: string }>;

  const featureCategories: FeatureCategory[] = [
    {
      category: 'Carnivore & Ketovore Diet Personalization',
      features: [
        {
          Icon: FaUtensils as FunctionComponent<{ className?: string }>,
          title: 'Carnivore Diet Recipes & Ketovore Meal Plans',
          description:
            'Access over 500 premium carnivore recipes and ketovore meal plans tailored to your meat-based eating preferences. From nose-to-tail carnivore dishes to animal-based low-carb meals, our recipes make your diet delicious and sustainable.',
          benefits: [
            'Over 500+ carnivore diet recipes and ketovore meal ideas',
            'Nose-to-tail carnivore cooking techniques and ancestral eating wisdom',
            'Customizable meal prep for your carnivore journey',
          ],
          screenshot: '/screenshots/recipes.jpg',
          screenshotAlt: 'EatMeat app showing carnivore recipes and meal plans interface'
        },
        {
          Icon: FaEdit as FunctionComponent<{ className?: string }>,
          title: 'Personalized Carnivore Diet Tracker',
          description:
            'Customize your carnivore diet plan with advanced tracking tools designed for elimination diets and identifying food sensitivities. Monitor your progress and optimize your meat-based eating plan effortlessly.',
          benefits: [
            'Track your carnivore eating plan and ketovore progress',
            'Elimination diet calendar for healing diet protocols',
            'Monitor carnivore benefits and ketosis levels',
          ],
          screenshot: '/screenshots/diet-tracker-screen.png',
          screenshotAlt: 'EatMeat app diet tracking interface showing progress charts'
        },
      ],
    },
    {
      category: 'Advanced Health & Weight Loss Tracking',
      features: [
        {
          Icon: FaWeight as FunctionComponent<{ className?: string }>,
          title: 'Weight Loss & Body Composition Monitoring',
          description:
            'Track your weight loss naturally with our comprehensive carnivore diet tracker. Monitor BMI, body composition, and sustainable weight loss progress to achieve your health goals.',
          benefits: [
            'Lose weight naturally with carnivore diet tracking',
            'Monitor BMI and body composition changes',
            'Track energy levels and mental clarity improvements',
          ],
        },
        {
          Icon: FaHeartbeat as FunctionComponent<{ className?: string }>,
          title: 'Hormonal Health & Digestive Wellness',
          description:
            'Log menstrual cycles and track how your carnivore or ketovore diet impacts hormonal balance and digestive health. Gain insights to optimize your well-being.',
          benefits: [
            'Correlate carnivore eating with hormonal improvements',
            'Track digestive health on meat-based eating plans',
            'Monitor well-being and nutrient density benefits',
          ],
        },
        {
          Icon: FaTrash as FunctionComponent<{ className?: string }>,
          title: 'Food Sensitivity & Elimination Tracking',
          description:
            'Perfect for elimination diets—track foods removed from your carnivore or ketovore plan to identify sensitivities and optimize your healing diet with detailed insights.',
          benefits: [
            'Elimination diet tracking for food sensitivities',
            'Identify foods affecting your carnivore benefits',
            'Optimize your healing diet with detailed insights',
          ],
        },
        {
          Icon: FaPlus as FunctionComponent<{ className?: string }>,
          title: 'New Food Introduction Tracker',
          description:
            'Safely introduce new foods to your carnivore or ketovore diet while monitoring their effects on your health goals and energy levels.',
          benefits: [
            'Safely expand your carnivore eating plan',
            'Track energy levels with new food additions',
            'Monitor nutrient density and ketosis maintenance',
          ],
        },
        {
          Icon: FaSmile as FunctionComponent<{ className?: string }>,
          title: 'Mental Clarity & Mood Tracking',
          description:
            'Track daily moods and mental clarity improvements from your carnivore diet journey and ketovore lifestyle to understand the impact of meat-based eating.',
          benefits: [
            'Monitor mental clarity from carnivore benefits',
            'Track mood improvements on ketovore diet',
            'Correlate well-being with your meat-based eating plan',
          ],
        },
      ],
    },
    {
      category: 'Carnivore Meal Planning & Expert Support',
      features: [
        {
          Icon: FaCalendar as FunctionComponent<{ className?: string }>,
          title: 'Carnivore Meal Planner & Diet Calendar',
          description:
            'Plan your carnivore meal prep and ketovore meals with our advanced diet calendar. Schedule your ancestral eating for optimal health goals and stay organized.',
          benefits: [
            'Weekly carnivore meal plans and ketovore meal prep',
            'Diet calendar for consistent ancestral eating',
            'Meal planning tools for sustainable carnivore journey',
          ],
        },
        {
          Icon: FaHeadset as FunctionComponent<{ className?: string }>,
          title: 'Expert Carnivore Diet Support',
          description:
            'Get 24/7 support for your carnivore diet questions, ketovore guidance, and dietary insights from our expert team to ensure success on your health journey.',
          benefits: [
            'Expert guidance for your carnivore journey',
            'Ketovore diet app support and troubleshooting',
            'Dietary insights for optimizing carnivore benefits',
          ],
        },
      ],
    },
  ];

  const colors = {
    primary: '#F97316',
    accent: '#EA580C',
    text: '#373636',
    border: '#D4D4D4',
    captionBg: '#E8ECEF',
    secondaryBg: '#F8DD91'
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.captionBg }}>
      {/* Features Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ color: colors.text }}>
              Explore EatMeat's
              <span className="block" style={{ color: colors.primary }}>Comprehensive Features</span>
            </h2>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed" style={{ color: colors.text }}>
              From personalized carnivore recipes to advanced health tracking, EatMeat empowers you to achieve your health goals with a seamless meat-based eating experience.
            </p>
          </div>

          {featureCategories.map((category, catIndex) => (
            <div key={catIndex} className="mb-24">
              <div className="text-center mb-16">
                <h3 className="text-3xl md:text-5xl font-bold mb-4" style={{ color: colors.primary }}>
                  {category.category}
                </h3>
                <div className="w-24 h-1 mx-auto rounded-full" style={{ backgroundColor: colors.accent }}></div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
                {category.features.map(({ Icon, title, description, benefits, screenshot, screenshotAlt }, index) => (
                  <div 
                    key={index} 
                    className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                    style={{ border: `2px solid ${colors.border}` }}
                  >
                    {/* Screenshot Section */}
                    {screenshot && (
                      <div className="relative h-64 md:h-80 overflow-hidden">
                        <img
                          src={screenshot}
                          alt={screenshotAlt}
                          className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => {
                            // Fallback to placeholder if image fails to load
                            e.currentTarget.src = `data:image/svg+xml;base64,${btoa(`
                              <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
                                <rect width="100%" height="100%" fill="${colors.captionBg}"/>
                                <rect x="50" y="50" width="300" height="200" rx="20" fill="white" stroke="${colors.border}" stroke-width="2"/>
                                <circle cx="200" cy="120" r="30" fill="${colors.primary}"/>
                                <rect x="120" y="170" width="160" height="8" rx="4" fill="${colors.border}"/>
                                <rect x="140" y="190" width="120" height="6" rx="3" fill="${colors.border}"/>
                                <text x="200" y="250" text-anchor="middle" fill="${colors.text}" font-family="Arial" font-size="14">App Screenshot</text>
                              </svg>
                            `)}`;
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>
                    )}
                    
                    <div className="p-8">
                      <div className="flex flex-col items-center text-center mb-6">
                        <div 
                          className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                          style={{ backgroundColor: colors.captionBg }}
                        >
                          <Icon className="h-10 w-10"  />
                        </div>
                        <h4 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: colors.text }}>
                          {title}
                        </h4>
                        <p className="text-lg leading-relaxed mb-6" style={{ color: colors.text }}>
                          {description}
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        {benefits.map((benefit, bIndex) => (
                          <div key={bIndex} className="flex items-start group/item">
                            <div 
                              className="w-6 h-6 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0 group-hover/item:scale-110 transition-transform"
                              style={{ backgroundColor: colors.primary }}
                            >
                              <FaCheckIcon className="h-3 w-3 text-white" />
                            </div>
                            <span className="text-base leading-relaxed" style={{ color: colors.text }}>
                              {benefit}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
});

export default Features;